import React, {useState, useContext, useRef, useEffect, useMemo} from 'react';
import {
    Table,
    TableHead,
    TableRow,
    TableHeader,
    TableBody,
    TableCell,
    TableContainer,
    Tag,
    Search,
    InlineNotification,
    Link,
    OverflowMenu,
    OverflowMenuItem
} from '@carbon/react';
import {observer} from 'mobx-react';
import ProcessingDetailsPanel from './processing-details-panel';
import {ResubmitFileModal} from './processing-modals/ResubmitFileModal';
import {DeleteFileModal} from './processing-modals/DeleteFileModal';
import {ProcessingFile} from '../../lib/types';
import {NetworkContext} from '../../lib/context';
import {fileProcessingStreamPersistence} from '../../../../lib/api';
import CustomPagination from '../../../../components/custom-pagination/CustomPagination';
import {ParsedApiError} from '../../../../lib/error-handler';
import ErrorDisplay from '../error-display';
import './processing-files.scss';

const StatusTag: React.FC<{status: string}> = React.memo(({status}) => {
    const statusConfig = {
        'needs-review': {type: 'red' as const, label: 'Needs Review'},
        'processing': {type: 'blue' as const, label: 'Processing'},
        'queued': {type: 'gray' as const, label: 'Queued'},
        'completed': {type: 'green' as const, label: 'Completed'},
        'uploading': {type: 'blue' as const, label: 'Uploading'},
    };

    const config = statusConfig[status as keyof typeof statusConfig] || {type: 'gray' as const, label: status};
    return <Tag type={config.type} size="sm" className="status-tag">{config.label}</Tag>;
});

interface FileTableRowProps {
    file: ProcessingFile;
    onRowClick: (fileId: string) => void;
    onDownloadErrorReport: (fileId: string) => void;
    onRenameFile: (fileId: string) => void;
    onResubmitFile: (fileId: string) => void;
    onDeleteFile: (fileId: string) => void;
}

const FileTableRow: React.FC<FileTableRowProps> = React.memo(({
    file,
    onRowClick,
    onDownloadErrorReport,
    onRenameFile,
    onResubmitFile,
    onDeleteFile
}) => {
    return (
        <TableRow key={file.id}>
            <TableCell>{file.fileName}</TableCell>
            <TableCell>{file.dateUploaded}</TableCell>
            <TableCell>{file.uploadedBy}</TableCell>
            <TableCell>
                <StatusTag status={file.status} />
            </TableCell>
            <TableCell>{file.stage}</TableCell>
            <TableCell>
                <Link onClick={() => onRowClick(file.id)}>View</Link>
            </TableCell>
            <TableCell>
                <OverflowMenu
                    size="sm"
                    flipped
                    aria-label={`Actions for ${file.fileName}`}
                >
                    {file.status === 'needs-review' && (
                        <OverflowMenuItem
                            itemText="Download Error Report"
                            onClick={() => onDownloadErrorReport(file.id)}
                        />
                    )}
                    <OverflowMenuItem
                        itemText="Rename"
                        onClick={() => onRenameFile(file.id)}
                    />
                    {file.status === 'needs-review' && (
                        <OverflowMenuItem
                            itemText="Resubmit"
                            onClick={() => onResubmitFile(file.id)}
                        />
                    )}
                    <OverflowMenuItem
                        itemText="Delete"
                        onClick={() => onDeleteFile(file.id)}
                        isDelete
                    />
                </OverflowMenu>
            </TableCell>
        </TableRow>
    );
}, (prevProps, nextProps) => {
    // Custom comparison: only re-render if this specific file's data changed
    return (
        prevProps.file.id === nextProps.file.id &&
        prevProps.file.fileName === nextProps.file.fileName &&
        prevProps.file.dateUploaded === nextProps.file.dateUploaded &&
        prevProps.file.uploadedBy === nextProps.file.uploadedBy &&
        prevProps.file.status === nextProps.file.status &&
        prevProps.file.stage === nextProps.file.stage
    );
});

export interface NeedsReviewBannerProps {
    count: number;
    show: boolean;
    onDismiss: () => void;
}

export const NeedsReviewBanner: React.FC<NeedsReviewBannerProps> = ({count, show, onDismiss}) => {
    if (count === 0 || !show) return null;

    return (
        <div className="processing-alert-wrapper">
            <InlineNotification
                kind="error"
                title={`${count} File${count > 1 ? 's' : ''} Needs Review`}
                subtitle="Review the error report, address issues found, and resubmit the revised file to continue processing."
                lowContrast
                onCloseButtonClick={onDismiss}
                className="processing-alert"
            />
        </div>
    );
};

export interface CompletedBannerProps {
    count: number;
    show: boolean;
    onDismiss: () => void;
    onViewFiles: () => void;
}

export const CompletedBanner: React.FC<CompletedBannerProps> = ({count, show, onDismiss, onViewFiles}) => {
    if (count === 0 || !show) return null;

    return (
        <div className="processing-alert-wrapper">
            <InlineNotification
                kind="success"
                title={`${count} File${count > 1 ? 's' : ''} Recently Completed`}
                subtitle="Completed files are ready to use in Available Files."
                lowContrast
                onCloseButtonClick={onDismiss}
                className="processing-alert"
            />
            <Link onClick={onViewFiles} className="processing-alert-action">
                View Available Files
            </Link>
        </div>
    );
};

interface ProcessingFilesProps {
    onSwitchToAvailableFiles?: () => void;
    error?: string | ParsedApiError | null;
}

const ProcessingFiles: React.FC<ProcessingFilesProps> = observer(({onSwitchToAvailableFiles, error}) => {
    const networkOptimizationStore = useContext(NetworkContext);
    const files = networkOptimizationStore.processingFiles;
    const loading = networkOptimizationStore.loadingProcessingFiles;
    const pagination = networkOptimizationStore.processingFilesPagination;

    const [selectedFileId, setSelectedFileId] = useState<string | null>(null);
    const selectedFile = selectedFileId ? files.find(f => f.id === selectedFileId) ?? null : null;
    const [isPanelOpen, setIsPanelOpen] = useState(false);
    const [loadingFileDetail, setLoadingFileDetail] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [showNeedsReviewBanner, setShowNeedsReviewBanner] = useState(true);
    const [showCompletedBanner, setShowCompletedBanner] = useState(true);
    const [pendingRename, setPendingRename] = useState(false);
    const [resubmitModalFile, setResubmitModalFile] = useState<ProcessingFile | null>(null);
    const [deleteModalFile, setDeleteModalFile] = useState<ProcessingFile | null>(null);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [deleteError, setDeleteError] = useState<string | undefined>(undefined);
    const paginationControllerRef = useRef<AbortController | null>(null);
    const connectingFileIdRef = useRef<string | null>(null); // Track file currently connecting

    useEffect(() => {
        return () => { paginationControllerRef.current?.abort(); };
    }, []);

    const headers = [
        {key: 'fileName', header: 'File Name'},
        {key: 'dateUploaded', header: 'Date Uploaded'},
        {key: 'uploadedBy', header: 'Uploaded By'},
        {key: 'status', header: 'Status'},
        {key: 'stage', header: 'Stage'},
        {key: 'view', header: ''},
        {key: 'actions', header: ''}
    ];

    const filteredFiles = useMemo(
        () => files.filter(file =>
            file.fileName.toLowerCase().includes(searchTerm.toLowerCase()) ||
            file.uploadedBy.toLowerCase().includes(searchTerm.toLowerCase())
        ),
        [files, searchTerm]
    );

    // Note: Counts are for current page only, not total across all pages
    const needsReviewCount = useMemo(() => files.filter(f => f.status === 'needs-review').length, [files]);
    const completedCount = useMemo(() => files.filter(f => f.status === 'completed').length, [files]);

    const handleRowClick = async (fileId: string) => {
        // CRITICAL: Prevent duplicate connection attempts
        if (connectingFileIdRef.current === fileId) {
            return;
        }

        const file = files.find(f => f.id === fileId);
        if (!file) {
            return;
        }

        // CRITICAL: Disconnect previous file's stream BEFORE setting new selectedFileId
        if (selectedFileId && selectedFileId !== fileId) {
            networkOptimizationStore.disconnectProcessingDetailsStream(parseInt(selectedFileId));
        }

        // Mark as connecting to block subsequent calls
        connectingFileIdRef.current = fileId;

        setSelectedFileId(fileId);
        setIsPanelOpen(true);

        setLoadingFileDetail(true);
        const abortController = new AbortController();
        try {
            const result = await networkOptimizationStore.loadFileDetail(parseInt(fileId), abortController);

            // Only connect to SSE if file details loaded successfully AND file is in active state
            if (result && (file.status === 'processing' || file.status === 'queued')) {
                networkOptimizationStore.connectToProcessingDetailsStream(
                    parseInt(fileId),
                    file.fileName,
                    result.events
                );
            }
        } catch (error: any) {
            if (error?.name !== 'AbortError') {
                networkOptimizationStore.showToastNotification(
                    'error',
                    'Failed to Load File Details',
                    'Unable to load complete file information. Please try again.'
                );
            }
        } finally {
            setLoadingFileDetail(false);
            // Clear connecting flag after operation completes
            connectingFileIdRef.current = null;
        }
    };

    const handleClosePanel = () => {
        setIsPanelOpen(false);
        setLoadingFileDetail(false);

        if (selectedFileId) {
            networkOptimizationStore.disconnectProcessingDetailsStream(parseInt(selectedFileId));
        }

        setTimeout(() => setSelectedFileId(null), 300);
    };

    const handleViewAvailableFiles = () => {
        if (onSwitchToAvailableFiles) {
            onSwitchToAvailableFiles();
        }
    };

    const handleDownloadErrorReport = (fileId: string) => {
        networkOptimizationStore.downloadErrorReport(Number(fileId));
    };

    const handleRenameFile = (fileId: string) => {
        const file = files.find(f => f.id === fileId);
        if (file) {
            setSelectedFileId(file.id);
            setIsPanelOpen(true);
            setPendingRename(true);
        }
    };

    const handleResubmitFile = (fileId: string) => {
        const file = files.find(f => f.id === fileId);
        if (file) setResubmitModalFile(file);
    };

    const handleDeleteFile = (fileId: string) => {
        const file = files.find(f => f.id === fileId);
        if (file) {
            setDeleteModalFile(file);
            setDeleteError(undefined);
        }
    };

    const handleDeleteConfirm = async () => {
        if (!deleteModalFile) return;
        setDeleteLoading(true);
        setDeleteError(undefined);
        const controller = new AbortController();
        try {
            await networkOptimizationStore.deleteFile(deleteModalFile.id, deleteModalFile.fileName, controller);
            setDeleteModalFile(null);
            if (selectedFileId === deleteModalFile.id) handleClosePanel();
        } catch (err: any) {
            setDeleteError(err?.apiError?.error?.message || err?.message || 'Could not delete the file. Please try again.');
        } finally {
            setDeleteLoading(false);
        }
    };

    const handlePageChange = (page: number) => {
        paginationControllerRef.current?.abort();
        paginationControllerRef.current = new AbortController();
        // CustomPagination passes 0-indexed pages, but store expects 1-indexed
        const apiPage = page + 1;
        networkOptimizationStore.loadProcessingFiles(paginationControllerRef.current, apiPage, pagination?.pageSize ?? 20);
    };

    const handlePageSizeChange = (size: number) => {
        paginationControllerRef.current?.abort();
        paginationControllerRef.current = new AbortController();
        networkOptimizationStore.loadProcessingFiles(paginationControllerRef.current, 1, size);
    };

    // Check if there are any files to display
    const hasFiles = files.length > 0;

    // Initial load: Load files and clean up stale streams from sessionStorage
    useEffect(() => {
        const abortController = new AbortController();
        const initialPage = pagination?.currentPage ?? 1;
        const initialPageSize = pagination?.pageSize ?? 20;
        networkOptimizationStore.loadProcessingFiles(abortController, initialPage, initialPageSize);

        // Clean up old streams from sessionStorage (but don't reconnect automatically)
        // Streams will connect only when user opens the details panel
        const streams = fileProcessingStreamPersistence.getAllStreams();
        Object.entries(streams).forEach(([streamId, data]) => {
            const maxAge = 2 * 60 * 60 * 1000; // 2 hours
            const age = Date.now() - data.timestamp;
            if (age > maxAge) {
                fileProcessingStreamPersistence.removeStream(streamId);
            }
        });

        return () => {
            networkOptimizationStore.disconnectAllFileStreams();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div className="processing-files-container">
            <NeedsReviewBanner
                count={needsReviewCount}
                show={showNeedsReviewBanner}
                onDismiss={() => setShowNeedsReviewBanner(false)}
            />
            <CompletedBanner
                count={completedCount}
                show={showCompletedBanner}
                onDismiss={() => setShowCompletedBanner(false)}
                onViewFiles={handleViewAvailableFiles}
            />

            {error ? (
                <ErrorDisplay error={error} title='Unable to Load Processing Files' />
            ) : loading && files.length === 0 ? (
                <div className="processing-loading-state">
                    <p>Loading processing files...</p>
                </div>
            ) : !hasFiles ? (
                <div className="processing-empty-state">
                    <p>You have no files currently processing. Uploaded files will appear here while they are being processed.</p>
                </div>
            ) : (
                <div className="processing-section">
                    <div className="processing-header">
                        <h3 className="processing-title">Processing</h3>
                        <p className="processing-subtitle">
                            Manage and track the status of files currently being processed by the system
                        </p>
                    </div>

                    <Search
                        placeholder="Search files"
                        labelText="Search files"
                        closeButtonLabelText="Clear search input"
                        size="lg"
                        className="processing-search"
                        value={searchTerm}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
                    />

                    <div className="processing-table-wrapper">
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        {headers.map(header => (
                                            <TableHeader key={header.key}>
                                                {header.header}
                                            </TableHeader>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {filteredFiles.map(file => (
                                        <FileTableRow
                                            key={file.id}
                                            file={file}
                                            onRowClick={handleRowClick}
                                            onDownloadErrorReport={handleDownloadErrorReport}
                                            onRenameFile={handleRenameFile}
                                            onResubmitFile={handleResubmitFile}
                                            onDeleteFile={handleDeleteFile}
                                        />
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </div>

                    {/* Pagination - show when pagination info is available */}
                    {pagination && (
                        <div className="processing-pagination">
                            <CustomPagination
                                totalItems={pagination.totalRows}
                                page={pagination.currentPage - 1} // Component uses 0-indexed pages
                                pageSize={pagination.pageSize}
                                onPageChange={handlePageChange}
                                onPageSizeChange={handlePageSizeChange}
                                pageSelectorType="Input"
                            />
                        </div>
                    )}
                </div>
            )}

            <ProcessingDetailsPanel
                file={selectedFile}
                isOpen={isPanelOpen}
                onClose={handleClosePanel}
                isLoadingDetails={loadingFileDetail}
                startRenaming={pendingRename}
                onRenamingStarted={() => setPendingRename(false)}
            />

            {resubmitModalFile && (
                <ResubmitFileModal
                    isOpen={true}
                    originalFileName={resubmitModalFile.originalFileName ?? ''}
                    uploadedAt={resubmitModalFile.uploadDetails.uploadedAt ?? ''}
                    fileSize={resubmitModalFile.uploadDetails.fileSize ?? 0}
                    onClose={() => setResubmitModalFile(null)}
                    onResubmit={(file) => {
                        networkOptimizationStore.startResubmit(resubmitModalFile.id, file);
                        setResubmitModalFile(null);
                    }}
                />
            )}

            <DeleteFileModal
                isOpen={!!deleteModalFile}
                fileName={deleteModalFile?.fileName ?? ''}
                uploadedAt={deleteModalFile?.uploadDetails.uploadedAt ?? ''}
                isLoading={deleteLoading}
                error={deleteError}
                onClose={() => { setDeleteModalFile(null); setDeleteError(undefined); }}
                onDelete={handleDeleteConfirm}
            />
        </div>
    );
});

export default ProcessingFiles;
